import { useMemo, useState } from 'react'
import { Info } from 'lucide-react'
import { MemberDetailDialog } from '../components/MemberDetailDialog'
import { MemberCard } from '../components/MemberCard'
import type { memberFilterSchema } from '@/schemas/filterSchemas'
import { Badge } from '@/components/custom/badge'
import { ListPageHeader } from '@/components/custom/ListPageHeader'
import { PaginationWrapper } from '@/components/custom/PaginationWrapper'
import { TBody, TD, TH, THead, TR, Table } from '@/components/custom/table'
import { Button } from '@/components/ui/button'
import { ScrollArea } from '@/components/ui/scroll-area'
import { useChurchesStore } from '@/features/churches/store/churchesStore'
import { useMembersStore } from '@/features/members/store/membersStore'
import { useAuthStore } from '@/features/auth/store/authStore'
import { usePermissions } from '@/utils/permissions'
import { useCleanQueryStates, usePagination } from '@/hooks'
import { formatPhone } from '@/utils/inputFormat'

export default function Members() {
  const { user, activeRole } = useAuthStore()
  const permissions = usePermissions()

  const churches = useChurchesStore((s) => s.churches)
  const members = useMembersStore((s) => s.members)

  const [query, setQuery] =
    useCleanQueryStates<typeof memberFilterSchema>('/authed/members')
  const [open, setOpen] = useState({ add: false, detail: false })
  const [memberId, setMemberId] = useState<string | null>(null)

  const filtered = useMemo(
    () =>
      members
        .filter((m) => {
          const matchSearch =
            query.search === '' ||
            m.name.toLowerCase().includes(query.search.toLowerCase()) ||
            m.email?.toLowerCase().includes(query.search.toLowerCase()) ||
            m.phone?.toLowerCase().includes(query.search.toLowerCase())

          const matchChurch =
            query.churchId.length === 0 || query.churchId.includes(m.churchId)

          const matchRole =
            query.roles.length === 0 || query.roles.includes(m.role)

          const matchCity =
            query.cities.length === 0 ||
            (m.address.city !== undefined &&
              query.cities.includes(m.address.city))

          const matchProvince =
            query.provinces.length === 0 ||
            (m.address.province !== undefined &&
              query.provinces.includes(m.address.province))

          const matchActive =
            !query.onlyActives || m.active === query.onlyActives

          return (
            matchSearch &&
            matchChurch &&
            matchCity &&
            matchProvince &&
            matchRole &&
            matchActive
          )
        })
        .sort((a, b) => a.name.localeCompare(b.name)),
    [members, query, user, activeRole],
  )

  const { page, pageCount, rows, setPage, goToFirstPage } = usePagination({
    data: filtered,
    pageSize: 10,
  })

  return (
    <div className="space-y-3">
      <ListPageHeader
        type="members"
        searchValue={query.search}
        onSearchChange={(value) => {
          setQuery({ search: value })
          goToFirstPage()
        }}
        initialFilters={query}
        onFilter={(filters) => {
          setQuery(filters)
          goToFirstPage()
        }}
        isFormOpen={open.add}
        onFormOpen={() => setOpen((o) => ({ ...o, add: true }))}
        onFormClose={() => setOpen((o) => ({ ...o, add: false }))}
        showAddButton={permissions.canCreateMember()}
      />

      {/* Desktop Table */}
      <div className="hidden lg:block overflow-hidden rounded-md border bg-white">
        <Table>
          <THead>
            <TR>
              <TH>Name</TH>
              <TH>Email</TH>
              <TH>Phone</TH>
              <TH>Church</TH>
              <TH>Status</TH>
              <TH className="text-right">Actions</TH>
            </TR>
          </THead>
          <TBody>
            {rows.map((member) => {
              const memberChurch = churches.find(
                (c) => c._id === member.churchId,
              )
              return (
                <TR key={member._id}>
                  <TD>{member.name}</TD>
                  <TD>{member.email}</TD>
                  <TD>{formatPhone(member.phone)}</TD>
                  <TD>{memberChurch?.name || 'Unassigned Church'}</TD>
                  <TD>
                    <Badge>{member.active ? 'Active' : 'Inactive'}</Badge>
                  </TD>
                  <TD className="text-right">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setMemberId(member._id)
                        setOpen((o) => ({ ...o, detail: true }))
                      }}
                    >
                      <Info className="h-4 w-4" /> Details
                    </Button>
                  </TD>
                </TR>
              )
            })}
            {rows.length === 0 && (
              <TR>
                <TD className="p-6 text-center text-neutral-500" colSpan={6}>
                  No results
                </TD>
              </TR>
            )}
          </TBody>
        </Table>
      </div>

      {/* Mobile Cards */}
      <ScrollArea className="lg:hidden h-[calc(100vh-170px)]">
        <div className="space-y-3">
          {filtered.length === 0 ? (
            <div className="bg-white rounded-lg border p-6 text-center text-neutral-500">
              No members found
            </div>
          ) : (
            filtered.map((member) => {
              const memberChurch = churches.find(
                (c) => c._id === member.churchId,
              )
              return (
                <MemberCard
                  key={member._id}
                  member={member}
                  churchName={memberChurch?.name || 'Unassigned Church'}
                  onDetail={(id) => {
                    setMemberId(id)
                    setOpen((o) => ({ ...o, detail: true }))
                  }}
                />
              )
            })
          )}
        </div>
      </ScrollArea>

      {/* Desktop Pagination */}
      <div className="hidden lg:block">
        <PaginationWrapper
          pageCount={pageCount}
          currentPage={page}
          onPageChange={setPage}
        />
      </div>

      <MemberDetailDialog
        memberId={memberId as string}
        open={open.detail}
        onClose={() => {
          setMemberId(null)
          setOpen((o) => ({ ...o, detail: false }))
        }}
      />
    </div>
  )
}
